import { AxiosError } from 'axios';

/**
 * API Error response structure
 */
interface ApiErrorResponse {
    success?: boolean;
    error?: string;
    details?: string | string[];
    existingUserId?: string;
}

/**
 * Extracts a user-friendly error message from an Axios error
 * 
 * @param err - The error object (typically from axios)
 * @param defaultMessage - Fallback message if no specific error is found
 * @returns User-friendly error message
 * 
 * @example
 * try {
 *   await axiosInstance.post('/api/users', data);
 * } catch (err) {
 *   setError(getErrorMessage(err, 'Failed to create user'));
 * }
 */
export const getErrorMessage = (
    err: unknown, 
    defaultMessage: string = 'An error occurred'
): string => {
    // Handle if err is already the data object (from old axios interceptor)
    if (err && typeof err === 'object' && 'error' in err && !('response' in err)) {
        return (err as ApiErrorResponse).error || defaultMessage;
    }
    
    const error = err as AxiosError<ApiErrorResponse>;
    
    // Debug logging (can remove in production)
    console.log('Error status:', error.response?.status);
    console.log('Error data:', error.response?.data);
    
    // Handle specific HTTP status codes
    if (error.response?.status === 409) {
        return error.response.data?.error || 'This resource already exists';
    }
    
    if (error.response?.status === 404) {
        return error.response.data?.error || 'Resource not found';
    }
    
    if (error.response?.status === 400) {
        const apiError = error.response.data;
        
        // Handle validation errors with details array
        if (Array.isArray(apiError?.details)) {
            return apiError.details.join(', ');
        }
        
        // Handle validation errors with details string
        if (apiError?.details) {
            return apiError.details;
        }
        
        return apiError?.error || 'Invalid request';
    }
    
    if (error.response?.status === 401) {
        return 'Unauthorized. Please log in again.';
    }
    
    if (error.response?.status === 403) {
        return 'You do not have permission to perform this action';
    }
    
    if (error.response?.status === 500) {
        return error.response.data?.error || 'Server error. Please try again later.';
    }
    
    // Extract error message from response data
    if (error.response?.data?.error) {
        return error.response.data.error;
    }
    
    // Extract error message from axios error
    if (error.message) {
        return error.message;
    }
    
    // Fallback to default message
    return defaultMessage;
};

/**
 * Gets a formatted error message with a prefix
 * Useful for showing context about what operation failed
 * 
 * @param err - The error object
 * @param operation - Description of the operation (e.g., "create user", "update query")
 * @returns Formatted error message
 * 
 * @example
 * try {
 *   await axiosInstance.delete('/api/users/123');
 * } catch (err) {
 *   setError(getFormattedError(err, 'delete user'));
 * }
 * // Returns: "Failed to delete user: User not found"
 */
export const getFormattedError = (
    err: unknown,
    operation: string
): string => {
    const errorMessage = getErrorMessage(err);
    return `Failed to ${operation}: ${errorMessage}`;
};

/**
 * Checks if an error is a specific HTTP status code
 * 
 * @param err - The error object
 * @param statusCode - HTTP status code to check
 * @returns true if error matches the status code
 * 
 * @example
 * try {
 *   await axiosInstance.post('/api/users', data);
 * } catch (err) {
 *   if (isErrorStatus(err, 409)) {
 *     setError('User already exists');
 *   }
 * }
 */
export const isErrorStatus = (err: unknown, statusCode: number): boolean => {
    const error = err as AxiosError;
    return error.response?.status === statusCode;
};
